// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-floating-promises, react-hooks/exhaustive-deps, react-hooks/immutability */

import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";

import { useHistory } from "react-router-dom";

import { Segment, Step, Icon, Loader } from "semantic-ui-react";

import { useModuleNavigation } from "../../../../hooks/useModuleNavigation";
import { useAuth0 } from "../../../../auth/auth0";
import { useUserAuth } from "../../../../hooks/useUserAuth";

import * as protocolActions from "../../../../redux/actions/protocolActions";

import ProtocolInfo from "../ProtocolInfo";
import Applications from "../Applications";
import Treatments from "../Treatments";
import Assessments from "../Assessments";
import AssociateTrials from "../AssociateTrials";
import Summary from "../Summary";

const ProtocolProcess = ({ activeStep, mode }) => {
	const history = useHistory();
	const moduleNavigation = useModuleNavigation();
	const { getTokenSilently } = useAuth0();
	const userAuth = useUserAuth();
	const dispatch = useDispatch();

	const [step] = useState(activeStep);
	const [loading, setLoading] = useState(true);
	const [failedToLoad, setFailedToLoad] = useState(false);

	const reduxProtocolData = useSelector((state) => (state.protocolData ? state.protocolData : null));

	useEffect(() => {
		if (mode === "new") {
			dispatch(protocolActions.resetProtocolData());
			setLoading(false);
		} else if (
			(reduxProtocolData?.id === null &&
				moduleNavigation.protocolId !== null &&
				moduleNavigation.activeSubmodule?.name !== "new") ||
			reduxProtocolData?.id !== moduleNavigation.protocolId
		) {
			getProtocolData();
		} else if (loading) {
			setLoading(false);
		}
	}, []);

	async function getProtocolData() {
		const accessToken = await getTokenSilently();
		dispatch(protocolActions.getProtocolData(moduleNavigation.protocolId, userAuth.currentClientId, accessToken))
			.then(() => {
				setLoading(false);
			})
			.catch(() => {
				setFailedToLoad(true);
				setLoading(false);
			});
	}

	return failedToLoad ? (
		<Segment style={{ margin: 15 }}>
			<h2>{"Failed to load protocol data. Please try again"}</h2>
		</Segment>
	) : loading ? (
		<Loader active />
	) : (
		<Segment basic id="protocol-process-steps" style={{ margin: 15, paddingBottom: "unset" }}>
			<Step.Group widths={6}>
				<Step
					link
					active={step === "info"}
					onClick={() =>
						history.push(
							moduleNavigation.createProtocolLink(
								true,
								null,
								moduleNavigation.protocolId && mode !== "new" ? "info" : "new",
							),
						)
					}
				>
					<Icon name="folder open" />
					<Step.Content>
						<Step.Title>Protocol Info</Step.Title>
						<Step.Description>Capture metadata and design settings</Step.Description>
					</Step.Content>
				</Step>

				<Step
					link
					active={step === "applications"}
					disabled={!moduleNavigation.protocolId || moduleNavigation.activeSubmodule?.name === "new"}
					onClick={() => history.push(moduleNavigation.createProtocolLink(true, null, "applications"))}
				>
					<Icon name="calendar check" />
					<Step.Content>
						<Step.Title>Applications</Step.Title>
						<Step.Description>Capture trial-specific field applications</Step.Description>
					</Step.Content>
				</Step>

				<Step
					link
					active={step === "treatments"}
					disabled={
						!moduleNavigation.protocolId ||
						moduleNavigation.activeSubmodule?.name === "new" ||
						reduxProtocolData?.applications?.length === 0
					}
					onClick={() => history.push(moduleNavigation.createProtocolLink(true, null, "treatments"))}
				>
					<Icon name="list ol" />
					<Step.Content>
						<Step.Title>Treatments</Step.Title>
						<Step.Description>Define specific treatment information</Step.Description>
					</Step.Content>
				</Step>

				<Step
					link
					active={step === "assessments"}
					disabled={
						!moduleNavigation.protocolId ||
						moduleNavigation.activeSubmodule?.name === "new" ||
						reduxProtocolData?.treatmentData?.length === 0
					}
					onClick={() => history.push(moduleNavigation.createProtocolLink(true, null, "assessments"))}
				>
					<Icon name="file" />
					<Step.Content>
						<Step.Title>Assessments</Step.Title>
						<Step.Description>Define ground data to be collected</Step.Description>
					</Step.Content>
				</Step>

				<Step
					link
					active={step === "associateTrials"}
					disabled={
						!moduleNavigation.protocolId ||
						moduleNavigation.activeSubmodule?.name === "new" ||
						reduxProtocolData?.treatmentData?.length === 0
					}
					onClick={() => history.push(moduleNavigation.createProtocolLink(true, null, "associate-trials"))}
				>
					<Icon className="chain" />
					<Step.Content>
						<Step.Title>Associate Trials</Step.Title>
						<Step.Description>Link existing trials for data aggregation</Step.Description>
					</Step.Content>
				</Step>

				<Step
					link
					active={step === "summary"}
					disabled={
						!moduleNavigation.protocolId ||
						moduleNavigation.activeSubmodule?.name === "new" ||
						reduxProtocolData?.treatmentData?.length === 0
					}
					onClick={() => history.push(moduleNavigation.createProtocolLink(true, null, "summary"))}
				>
					<Icon name="clipboard" />
					<Step.Content>
						<Step.Title>Summary</Step.Title>
						<Step.Description>Review multi-trial linkages and finalize protocol</Step.Description>
					</Step.Content>
				</Step>
			</Step.Group>
			<>
				<Segment id="flight-process-step-content" basic style={{ marginTop: "unset", padding: 0, minHeight: 70 }}>
					{(() => {
						switch (step) {
							default:
							case "info":
								return <ProtocolInfo mode={moduleNavigation.protocolId && mode !== "new" ? "edit" : "new"} />;
							case "applications":
								return <Applications />;
							case "treatments":
								return <Treatments />;
							case "assessments":
								return <Assessments />;
							case "associateTrials":
								return (
									<AssociateTrials
										growingSeasonId={reduxProtocolData?.growingSeasonId}
										cropId={reduxProtocolData?.cropId}
										numberOfTreatments={reduxProtocolData?.treatments}
									/>
								);
							case "summary":
								return <Summary />;
						}
					})()}
				</Segment>
			</>
		</Segment>
	);
};

ProtocolProcess.propTypes = {
	activeStep: PropTypes.string.isRequired,
	mode: PropTypes.string.isRequired,
};

export default ProtocolProcess;
